// 工事マスタの一覧表示。
// 工事 1 件につきアコーディオンを 1 行並べ、開くと工事の情報を項目一覧(表形式)で見せる。
// 見出し行は他のマスタ画面と同じ共通部品を使い、見た目を揃えている。

import {
  Box,
  Stack,
  Typography,
  Chip,
  Paper,
  Alert,
  Accordion,
  AccordionDetails,
  Button,
  IconButton,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import EngineeringIcon from "@mui/icons-material/Engineering";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { Fragment, useState } from "react";
import { ExtendedDB } from "../../../types/extendedDB";
import { MasterAccordionSummary } from "./MasterAccordionSummary";
import { OfflineMasterBanner } from "../../../components/OfflineMasterBanner";
import { EmptyState } from "../../../components/EmptyState";
import { WORD } from "../../../word";
import { renderWord } from "../../../util/renderWord";
import { FULL_EPS } from "../../../util/consumption";
import { readProp } from "../../../util/typed";
import type { WorkBase } from "../../../schema/generated/Work";

// 工事 1 件あたりの材料の進み具合。
type WorkMaterialSummary = {
  planned: number;   // 入荷予定: その工事の搬入イベントに登録された予定数の合計
  carriedIn: number; // 入荷済: 搬入イベントに紐付いた材料QRの数
  consumed: number;  // 施工済: 完了した施工で全量使い切られた材料QRの数
};

// 一覧の 1 行に出す表の項目。
type WorkInfoItem = {
  label: string;
  value: string;
};

function summarizeWorkMaterials(db: ExtendedDB, workId: number): WorkMaterialSummary {
  const carryIns = db.carryIns.filter((c) => c.work_id === workId && c.deleted_at == null);
  const planned = carryIns.reduce((total, c) => total + c.planned_count, 0);
  const carryInIds = new Set(carryIns.map((c) => c.id));
  const carriedIn = db.materialQrs.filter((q) => q.carry_in_id != null && carryInIds.has(q.carry_in_id)).length;

  const areas = db.constructionPlanAreas.filter((a) => a.work_id === workId && a.deleted_at == null);
  const areaIds = new Set(areas.map((a) => a.id));
  const constructionIds = new Set(
    db.processExecutions
      .filter((p) => {
        // 旧キャッシュ行は construction_plan_area_id ではなく area_id を持つことがあるため両対応する。
        const areaId = p.construction_plan_area_id ?? readProp(p, "area_id");
        return typeof areaId === "string" && areaIds.has(areaId) && p.completed_at != null;
      })
      .map((p) => p.construction_id),
  );
  const finishedConstructionIds = new Set(
    db.constructions.filter((c) => c.deleted_at == null && c.finished_at != null && constructionIds.has(c.id)).map((c) => c.id),
  );

  // 施工済 = 「完了した施工が実際に使った(used_ratio>0)」材料QRのうち、使用割合の合計が全量に達したものの数。
  // 全量かどうかは消費記録(真ソース)から都度計算する。
  const aliveConsumptions = db.constructionMaterialConsumptions.filter((c) => c.deleted_at == null);
  const ratioByQrId = new Map<number, number>();
  const qrIdsConsumedByFinished = new Set<number>();
  for (const c of aliveConsumptions) {
    const ratio = c.used_ratio ?? 1;
    ratioByQrId.set(c.material_qr_id, (ratioByQrId.get(c.material_qr_id) ?? 0) + ratio);
    if (ratio > 0 && finishedConstructionIds.has(c.construction_id)) {
      qrIdsConsumedByFinished.add(c.material_qr_id);
    }
  }
  const consumed = Array.from(qrIdsConsumedByFinished).filter((qrId) => (ratioByQrId.get(qrId) ?? 0) >= 1 - FULL_EPS).length;

  return { planned, carriedIn, consumed };
}

// その工事の工事型区分に設定されている場所区分ラベルの数。
function countLocationLabels(db: ExtendedDB, areaDivisionId: number): number {
  return db.areaDivisionLocationLabels.filter(
    (l) => l.area_division_id === areaDivisionId && l.deleted_at == null && l.label_name !== "",
  ).length;
}

function orDash(value: string): string {
  if (value !== "") {
    return value;
  }
  return "-";
}

// 発注者は「組織名（部署名）」の形にまとめる。部署が空なら組織名だけ出す。
function clientText(clientName: string, department: string): string {
  if (department !== "") {
    return `${clientName}（${department}）`;
  }
  return clientName;
}

function clientNameOf(db: ExtendedDB, clientId: number): string {
  return db.clients.find((c) => c.id === clientId)?.name ?? "-";
}

// 見出しの先頭に出す整理番号。未入力の工事は代わりに工事IDを見せる。
function controlNumberLabel(work: WorkBase): string {
  if (work.control_number !== "") {
    return work.control_number;
  }
  return `#${work.id}`;
}

function displayNameOf(work: WorkBase): string {
  if (work.short_name !== "") {
    return work.short_name;
  }
  return orDash(work.official_name);
}

// 展開時に出す項目一覧。内側の配列が表の 1 行にあたり、1 行に最大 2 組の「項目名・値」を並べる。
function buildWorkInfoRows(db: ExtendedDB, work: WorkBase, labelCount: number): WorkInfoItem[][] {
  const orderer = clientText(clientNameOf(db, work.client_id), work.client_department);
  const period = `${orDash(work.period_start)} 〜 ${orDash(work.period_end)}`;
  return [
    [{ label: "正式名称", value: orDash(work.official_name) }],
    [{ label: "発注者", value: orderer }, { label: "工期", value: period }],
    [{ label: "受注者", value: orDash(work.contractor_name) }, { label: "塗装種類", value: orDash(work.paint_kind) }],
    [{ label: "構造形式", value: orDash(work.structure_form) }, { label: "場所区分ラベル数", value: `${labelCount}` }],
    [{ label: "工事場所", value: orDash(work.location) }],
  ];
}

// 1 組だけの行は値を残りの列まで広げて、長い文章が折り返しすぎないようにする。
function valueColSpanOf(itemsInRow: number): number {
  if (itemsInRow === 1) {
    return 3;
  }
  return 1;
}

const labelCellSx = { width: 140, color: "text.secondary", fontWeight: 600, bgcolor: "grey.100" } as const;

export function WorkListView({
  db,
  isOnline,
  showOfflineBanner,
  onSelect,
  onCreateNew,
}: {
  db: ExtendedDB;
  isOnline: boolean;
  showOfflineBanner: boolean;
  onSelect: (id: number) => void;
  onCreateNew: () => void;
}) {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const handleExpand = (id: number) => {
    setExpandedId((prev) => {
      if (prev === id) {
        return null;
      }
      return id;
    });
  };

  const activeWorks = db.works.filter((w) => w.deleted_at == null);

  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="center" gap={1}>
        <EngineeringIcon color="action" />
        <Typography variant="h6" fontWeight="bold">工事マスタ</Typography>
        <Chip size="small" color="info" label={`${activeWorks.length} 件`} />
        <Box flex={1} />
        <Button variant="contained" size="small" startIcon={<AddIcon />} disabled={!isOnline} onClick={onCreateNew}>
          新規工事作成
        </Button>
      </Stack>
      {showOfflineBanner && <OfflineMasterBanner isOnline={isOnline} />}
      <Alert severity="info" sx={{ py: 0.5 }}>{renderWord(WORD.workMaster.listInfo)}</Alert>

      <Paper variant="outlined">
        <Box sx={{ overflowX: "auto" }}>
          {activeWorks.length === 0 && (
            <EmptyState message="工事が登録されていません。「新規工事作成」から登録してください。" py={4} />
          )}
          {activeWorks.map((work) => {
            const summary = summarizeWorkMaterials(db, work.id);
            const labelCount = countLocationLabels(db, work.area_division_id);
            const infoRows = buildWorkInfoRows(db, work, labelCount);
            return (
              <Accordion
                key={work.id}
                disableGutters
                expanded={expandedId === work.id}
                onChange={() => handleExpand(work.id)}
              >
                <MasterAccordionSummary
                  wrap
                  idLabel={controlNumberLabel(work)}
                  title={<Typography variant="body2" fontWeight={600} sx={{ minWidth: 220 }}>{displayNameOf(work)}</Typography>}
                  chips={<>
                    <Chip size="small" variant="outlined" color="secondary" label={`場所区分ラベル ${labelCount}`} />
                    <Chip size="small" variant="outlined" label={`予定 ${summary.planned}`} />
                    <Chip size="small" variant="outlined" color="primary" label={`入荷 ${summary.carriedIn}`} />
                    <Chip size="small" variant="outlined" color="success" label={`施工済 ${summary.consumed}`} />
                    <Box flex={1} />
                  </>}
                  actions={
                    <IconButton size="small" title="編集を開く" disabled={!isOnline} onClick={() => onSelect(work.id)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  }
                />
                <AccordionDetails sx={{ backgroundColor: "grey.50" }}>
                  <Stack gap={1.5}>
                    <Table size="small">
                      <TableBody>
                        {infoRows.map((items) => {
                          const valueColSpan = valueColSpanOf(items.length);
                          const rowKey = items.map((item) => item.label).join("-");
                          return (
                            <TableRow key={rowKey}>
                              {items.map((item) => (
                                <Fragment key={item.label}>
                                  <TableCell sx={labelCellSx}>{item.label}</TableCell>
                                  <TableCell colSpan={valueColSpan}>{item.value}</TableCell>
                                </Fragment>
                              ))}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    <Stack direction="row" justifyContent="flex-end">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<EditIcon />}
                        disabled={!isOnline}
                        onClick={() => onSelect(work.id)}
                      >
                        編集を開く
                      </Button>
                    </Stack>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Paper>
    </Stack>
  );
}
